import * as React from 'react';
import './PasswordStrengthMeter.scss';
import { ProgressIndicator } from 'office-ui-fabric-react/lib/ProgressIndicator';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import stringsResx from '../../Share.resx';

const passwordStrengthStrings =
  [stringsResx.passwordStrengthMeterStrengthLevel0,
  stringsResx.passwordStrengthMeterStrengthLevel1,
  stringsResx.passwordStrengthMeterStrengthLevel2,
  stringsResx.passwordStrengthMeterStrengthLevel3,
  stringsResx.passwordStrengthMeterStrengthLevel4];

export interface IPasswordStrengthMeterState {
}

export interface IPasswordStrengthMeterProps {
  currentPassword: string;
  getStrengthMeterValue?: (passwordStrength: String) => void;
}

export class PasswordStrengthMeter extends React.Component<IPasswordStrengthMeterProps, IPasswordStrengthMeterState> {
  constructor(props: IPasswordStrengthMeterProps) {
    super(props);
    this._calculatePasswordStrength = this._calculatePasswordStrength.bind(this);
    this._renderStrengthText = this._renderStrengthText.bind(this);
    this._renderStrengthBar = this._renderStrengthBar.bind(this);
  }

  public render(): React.ReactElement<{}> {
    const currentPasswordStrength = this._calculatePasswordStrength(this.props.currentPassword);
    const toolTipText = StringHelper.format(stringsResx.currentStrength, passwordStrengthStrings[currentPasswordStrength]);
    if (this.props.getStrengthMeterValue) {
      this.props.getStrengthMeterValue(passwordStrengthStrings[currentPasswordStrength]);
    }
    return (
      <div className='od-PasswordStrengthMeter'>
        <TooltipHost
          tooltipProps={ {
            maxWidth: '300px',
            onRenderContent: () => {
              return (
                <div>
                  <ul style={ { margin: 0, padding: 0 } }>
                    <div className={ 'od-PasswordStrengthMeter-StrengthExplanation' }>
                      { stringsResx.passwordStrengthMeterStrengthExplanation1 } <br></br>
                    </div>
                    { stringsResx.passwordStrengthMeterStrengthExplanation2 } <br></br>
                    { stringsResx.passwordStrengthMeterStrengthExplanation3 } <br></br>
                    { stringsResx.passwordStrengthMeterStrengthExplanation4 }
                  </ul>
                </div>
              );
            }
          } }
        >
          <div className='od-PasswordStrengthMeter-test' tabIndex={ 0 } aria-label={ toolTipText }>
            { this._renderStrengthText(currentPasswordStrength) }
            { this._renderStrengthBar(currentPasswordStrength) }
          </div>
        </TooltipHost>
      </div>
    );
  }

  private _renderStrengthText(passwordStrength: number) {
    const passwordStrengthLevelText = passwordStrengthStrings[passwordStrength];
    return (
      <div className={ 'od-PasswordStrengthMeter-StrengthNotificationText' }>
        <div className={ 'od-PasswordStrengthMeter-StrengthNotificationText-StrengthText' }>
          { stringsResx.passwordStrengthMeterStrengthBarText }
        </div>
        <div className={ 'od-PasswordStrengthMeter-StrengthNotificationText' + ' ' + passwordStrengthLevelText }>
          { passwordStrengthLevelText }
        </div>
      </div >
    );
  }

  private _renderStrengthBar(passwordStrength: number) {
    const passwordStrengthLevelText = passwordStrengthStrings[passwordStrength];
    return (
      <div className={ 'od-PasswordStrengthMeter-StrengthNotificationBar' + ' ' + passwordStrengthLevelText } aria-hidden= {true} role='presentation'>
        <ProgressIndicator
          percentComplete={ passwordStrength * .25 }
          barHeight={ 4 }
        />
      </div >
    );
  }

  private _calculatePasswordStrength(currentPassword: string) {
    let strengthScore = 0;
    // Check if password actually exists
    if (currentPassword.length > 0) {
      strengthScore++;
    }
    // Check if we have both upper and lower case
    if (/[A-Z]/.test(currentPassword) && /[a-z]/.test(currentPassword)) {
      strengthScore++;
    }
    // Check if password contains a number
    if (/\d/.test(currentPassword)) {
      strengthScore++;
    }
    // Check if password contains special characters (non alphanumeric)
    if (/[^A-Za-z0-9]/g.test(currentPassword)) {
      strengthScore++;
    }
    return strengthScore;
  }
}